// src/services/whsInspectionsService.ts
// Fetches live WHS inspections from the Cloudflare Worker API (Neon database).
// Maps to the WHSInspection shape WHSInspections.tsx expects.
//
// Architecture: seed-from-API (same as timesheetsService).
// WHSInspections.tsx uses useState(MOCK_WHS_INSPECTIONS) and adds inspections
// locally via handleNewInspection. On first load, Neon inspections merge in.
// IDs never clash (Neon UUIDs vs 'ins_001' mock IDs).
//
// Field notes:
//   whsInspections.title       → used to infer type (or default DAILY_PRESTART)
//   whsInspections.overallPass → true→PASS, false→FAIL, null→null
//   whsInspections.checklist   → jsonb; maps to items[] if in { label, result, category } format
//   issues[]                   → empty (not yet stored separately in Neon)

import { api } from '../utils/api'

interface ApiInspection {
  id:           string
  jobId?:       string
  job_id?:      string
  title?:       string
  inspectorId?: string | null
  inspector_id?: string | null
  inspectedAt?: string | null
  inspected_at?: string | null
  checklist?:   any[] | null     // jsonb
  overallPass?: boolean | null
  overall_pass?: boolean | null
  notes?:       string | null
  photoUrls?:   string[] | null
  geoLat?:      string | null
  geoLng?:      string | null
  closedAt?:    string | null
  closed_at?:   string | null
  createdAt?:   string
  created_at?:  string
}

// What WHSInspections.tsx expects — matches the local WHSInspection interface
export interface MappedWHSInspection {
  id:                   string
  inspectionNumber:     string
  type:                 string
  jobId:                string
  jobNumber:            string
  conductedById:        string
  conductedByName:      string
  conductedAt:          string
  result:               'PASS' | 'CONDITIONAL' | 'FAIL' | null
  status:               'DRAFT' | 'SUBMITTED' | 'CLOSED'
  positiveObservations: string[]
  notes?:               string
  items: {
    id:      string
    label:   string
    category?: string
    result:  'OK' | 'ISSUE' | 'NA' | null
    notes?:  string
  }[]
  issues: never[]
}

function mapOverallPass(pass: boolean | null | undefined): 'PASS' | 'CONDITIONAL' | 'FAIL' | null {
  if (pass === true)  return 'PASS'
  if (pass === false) return 'FAIL'
  return null
}

function inferType(title: string | undefined): string {
  const t = (title ?? '').toLowerCase()
  if (t.includes('pre-start') || t.includes('prestart')) return 'DAILY_PRESTART'
  if (t.includes('safety walk'))                          return 'SAFETY_WALK'
  if (t.includes('plant'))                                return 'PLANT_INSPECTION'
  if (t.includes('electrical'))                           return 'ELECTRICAL_INSTALL'
  if (t.includes('excavat'))                              return 'EXCAVATION_CHECK'
  if (t.includes('confined'))                             return 'CONFINED_SPACE_PREENTRY'
  if (t.includes('weekly') || t.includes('audit'))        return 'WEEKLY_SITE_AUDIT'
  return 'DAILY_PRESTART'
}

function mapApiInspection(raw: ApiInspection): MappedWHSInspection {
  const conductedAt  = raw.inspectedAt ?? raw.inspected_at ?? new Date().toISOString()
  const checklist    = (raw.checklist ?? []) as any[]

  return {
    id:               raw.id,
    inspectionNumber: `INS-${raw.id.slice(-6).toUpperCase()}`,
    type:             inferType(raw.title),
    jobId:            raw.jobId ?? raw.job_id ?? '',
    jobNumber:        '',
    conductedById:    raw.inspectorId ?? raw.inspector_id ?? '',
    conductedByName:  '',
    conductedAt,
    result:           mapOverallPass(raw.overallPass ?? raw.overall_pass),
    status:           raw.closedAt ?? raw.closed_at ? 'CLOSED' : 'SUBMITTED',
    positiveObservations: [],
    notes:            raw.notes ?? undefined,
    items: checklist.map((item: any, idx: number) => ({
      id:       item.id ?? `item_${idx}`,
      label:    item.label ?? item.item ?? String(item),
      category: item.category,
      result:   item.result ?? item.status ?? null,
      notes:    item.notes,
    })),
    issues: [],
  }
}

export async function fetchWHSInspections(jobId?: string): Promise<MappedWHSInspection[]> {
  const params = jobId ? `?jobId=${jobId}` : ''
  const { data } = await api.get<{ data: ApiInspection[] } | ApiInspection[]>(`/api/whs/inspections${params}`)
  const raw: ApiInspection[] = Array.isArray(data) ? data : ((data as any).data ?? [])
  return raw.map(mapApiInspection)
}
